import React from 'react';
import { Brand, TaniaCard } from './components';
import { candidateCvFor, useContent } from './content';
import { Markdown } from './markdown';

function SectionReferences({ sources }: { sources: ReadonlyArray<{ label: string; url: string }> }) {
  if (sources.length === 0) return null;
  return (
    <section className="section-refs" aria-label="References">
      <h3>References</h3>
      <ol>
        {sources.map((source, index) => (
          <li key={index}>
            {source.url ? <a href={source.url} rel="noopener noreferrer" target="_blank">{source.label || source.url}</a> : source.label}
          </li>
        ))}
      </ol>
    </section>
  );
}
import { isRolePlay, SCENARIOS, type InterviewSetup } from './types';

const CV_ID = '__candidate_cv';

export function PrepPack({
  embedded = false,
  onBack,
  backLabel,
  setup,
}: {
  backLabel: string;
  embedded?: boolean;
  onBack: () => void;
  setup: InterviewSetup;
}) {
  const content = useContent();
  const cv = candidateCvFor(setup, content);
  const sections = React.useMemo(
    () => [
      ...(cv ? [{ id: CV_ID, title: `Candidate CV — ${cv.label}`, markdown: cv.text, sources: [] }] : []),
      ...(content?.prepPack ?? []),
    ],
    [cv, content],
  );
  const [activeId, setActiveId] = React.useState(sections[0]?.id ?? '');
  const active = sections.find((section) => section.id === activeId) ?? sections[0];
  const scenario = SCENARIOS.find((item) => item.value === setup.scenario);

  return (
    <main className={embedded ? 'prep-page prep-page--embedded' : 'prep-page'}>
      {!embedded && (
        <header className="app-header">
          <Brand compact />
          <button className="text-button" onClick={onBack} type="button">{backLabel}</button>
        </header>
      )}
      <section className="prep-layout">
        <div className="prep-heading">
          <p className="eyebrow">Prep pack</p>
          <h1>Before you dial in</h1>
          {isRolePlay(setup.scenario) ? (
            <p>
              The candidate CV is what you would hold in the real call. Anything the candidate
              is hiding stays hidden until the debrief.
            </p>
          ) : (
            <p>Reference notes for “{scenario?.label}”.</p>
          )}
        </div>
        <nav aria-label="Prep pack sections" className="prep-nav">
          {sections.map((section) => (
            <button
              aria-current={section.id === active?.id}
              className={section.id === active?.id ? 'is-active' : ''}
              key={section.id}
              onClick={() => setActiveId(section.id)}
              type="button"
            >
              {section.title}
            </button>
          ))}
        </nav>
        <article className="prep-content">
          <TaniaCard detailed />
          {active ? (
            <>
              <h2>{active.title}</h2>
              <Markdown citations={active.sources} source={active.markdown} />
              <SectionReferences sources={active.sources} />
            </>
          ) : (
            <p>No prep material is available.</p>
          )}
        </article>
      </section>
    </main>
  );
}
